import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../services/api';
import { availabilityObjToArray, formatTimeSlot } from '../utils/dateUtils';
import Input from '../components/ui/Input';
import Button from '../components/ui/Button';
import { Calendar, Clock, Users, BookOpen, Book, Bookmark } from 'lucide-react';

/**
 * Page for confirming user information and completing registration/request
 */
const ConfirmationPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get data from location state
  const { mode, classData, classType, classTypeId, availability } = location.state || {};
  
  // Redirect if accessing directly without state
  useEffect(() => {
    if (!mode) {
      navigate('/classes');
    }
  }, [mode, navigate]);
  
  // Form state
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: user?.phone || ''
  });
  
  // Loading and error states
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  
  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setIsSubmitting(true);
      setError(null);
      
      // Validate form
      if (!formData.name || !formData.email || !formData.phone) {
        setError('Please fill in all fields');
        return;
      }
      
      if (mode === 'join') {
        // Join the class - use $id which is Appwrite's standard ID field
        await api.classes.join(
          classData.$id,  // Changed from classData.id to classData.$id
          user.$id,       // Also ensure we're using user.$id
          formData.name,
          formData.email,
          formData.phone
        );
        
        // Redirect to success page
        navigate('/success', {
          state: {
            mode: 'join',
            classData
          }
        });
      } else if (mode === 'availability') {
        // Convert availability object to array
        const availabilityArray = availabilityObjToArray(availability);

        if (!classTypeId) {
          setError('Class type information is missing. Please try again.');
          return;
        }
        
        // Submit availability
        await api.availability.submit(
          user.$id,
          classTypeId,
          availabilityArray,
          formData.name,
          formData.email,
          formData.phone
        );
        
        // Redirect to success page
        navigate('/success', {
          state: {
            mode: 'availability',
            classTypeId,
            classType,
            availability
          }
        });
      }
    } catch (err) {
      console.error('Submission error:', err);
      setError(err.message || 'An error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Handle back button
  const handleBack = () => {
    if (mode === 'join') {
      navigate('/classes');
    } else {
      navigate('/availability', { 
        state: { 
          classType, 
          classTypeId 
        } 
      });
    }
  };
  
  // Format page title and button text based on mode
  const getPageTitle = () => {
    if (mode === 'join') {
      return `Join ${classData?.classTypeName || ''} Class`;
    }
    return 'Submit Your Availability';
  };
  
  const getButtonText = () => {
    if (mode === 'join') {
      return 'Join Class';
    }
    return 'Submit Request';
  };
  
  // Calculate progress percentage for the members bar
  const getMembersPercentage = () => {
    if (!classData || !classData.members || !classData.totalSpots) return 0;
    return (classData.members.length / classData.totalSpots) * 100;
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 to-gray-950 text-white">
      {/* Header with improved styling */}
      <header className="bg-gray-900 border-b border-gray-800 sticky top-0 z-10 shadow-md">
        <div className="container mx-auto p-4 flex justify-between items-center">
          <h1 className="text-xl font-bold">
            <span className="bg-gradient-to-r from-purple-400 to-indigo-500 text-transparent bg-clip-text">
              Join a Group Class
            </span>
          </h1>
          
          <div className="flex items-center">
            <span className="text-gray-300 text-sm hidden sm:inline">
              Hello, {user?.name}
            </span>
          </div>
        </div>
        {/* Add a decorative accent line */}
        <div className="h-1 bg-gradient-to-r from-purple-600 via-indigo-500 to-purple-600"></div>
      </header>
      
      {/* Main content with improved spacing and visual design */}
      <main className="container mx-auto py-8 px-4">
        <div className="max-w-md mx-auto">
          {/* Page title with Chinese character decoration */}
          <div className="text-center mb-8">
            {/* Chinese characters - different text based on mode */}
            <div className="inline-block text-6xl font-light text-purple-400 opacity-20 mb-2">
              {mode === 'join' 
                ? '参加课程' // "Join Class" in Chinese
                : '提交时间' // "Submit Time/Availability" in Chinese
              }
            </div>
            <h2 className="text-2xl font-bold">
              {mode === 'join' 
                ? `Join the ${classData?.classTypeName || ''} Class` 
                : 'Submit Your Availability'}
            </h2>
            <div className="w-16 h-1 bg-purple-500 mx-auto mt-2"></div>
          </div>
          
          {/* Error message */}
          {error && (
            <div className="p-4 bg-red-900 bg-opacity-20 border border-red-700 rounded-lg mb-6">
              <p className="text-red-400">{error}</p>
            </div>
          )}
          
          {/* Class details or availability info */}
          <div className="bg-gray-800 rounded-lg border border-gray-700 shadow-lg overflow-hidden mb-8">
            <div className="bg-gradient-to-r from-gray-800 to-gray-700 px-6 py-3 border-b border-gray-700">
              <h3 className="font-semibold text-lg">
                {mode === 'join' ? 'Class Details' : 'Your Selected Times'}
              </h3>
            </div>
            
            <div className="p-6">
              {mode === 'join' && classData && (
                <div className="space-y-4">
                  <div className="flex items-center">
                    <div className="w-8 text-purple-400">
                      {typeof Calendar !== 'undefined' ? <Calendar size={20} /> : null}
                    </div>
                    <div>
                      <p className="text-gray-400 text-sm">Day</p>
                      <p className="font-medium">{classData.day}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center">
                    <div className="w-8 text-purple-400">
                      {typeof Clock !== 'undefined' ? <Clock size={20} /> : null}
                    </div>
                    <div>
                      <p className="text-gray-400 text-sm">Time</p>
                      <p className="font-medium">{classData.time}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center">
                    <div className="w-8 text-purple-400">
                      {typeof Users !== 'undefined' ? <Users size={20} /> : null}
                    </div>
                    <div className="flex-1">
                      <p className="text-gray-400 text-sm">Spots Left</p>
                      <p className="font-medium">{classData.totalSpots - classData.members.length} out of {classData.totalSpots}</p>
                      {/* Progress bar for members */}
                      <div className="w-full bg-gray-700 rounded-full h-2 mt-2">
                        <div 
                          className="bg-purple-500 h-2 rounded-full" 
                          style={{ width: `${getMembersPercentage()}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              {mode === 'availability' && (
                <div className="space-y-4">
                  <div className="flex items-center">
                    <div className="w-8 text-purple-400">
                      {/* Book or Bookmark icon would be better for class type */}
                      {typeof BookOpen !== 'undefined' ? <BookOpen size={20} /> : 
                       typeof Book !== 'undefined' ? <Book size={20} /> : 
                       typeof Bookmark !== 'undefined' ? <Bookmark size={20} /> : null}
                    </div>
                    <div>
                      <p className="text-gray-400 text-sm">Class Type</p>
                      <p className="font-medium">{classType?.charAt(0).toUpperCase() + classType?.slice(1)}</p>
                    </div>
                  </div>
                  
                  <div>
                    <div className="flex items-start mb-2">
                      <div className="w-8 text-purple-400">
                        {typeof Clock !== 'undefined' ? <Clock size={20} /> : null}
                      </div>
                      <p className="text-gray-400 text-sm">Available Times</p>
                    </div>
                    
                    <ul className="ml-8 space-y-2">
                      {availabilityObjToArray(availability).map((slot, index) => (
                        <li key={index} className="flex items-start">
                          <span className="text-purple-400 mr-2">•</span>
                          <span className="font-medium">{formatTimeSlot(slot)}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
            </div>
          </div>
          
          {/* Contact information form with better styling */}
          <form onSubmit={handleSubmit}>
            <div className="bg-gray-800 rounded-lg border border-gray-700 shadow-lg overflow-hidden">
              <div className="bg-gradient-to-r from-gray-800 to-gray-700 px-6 py-3 border-b border-gray-700">
                <h3 className="font-semibold text-lg">Your Information</h3>
              </div>
              
              <div className="p-6 space-y-5">
                {/* Use your existing Input component, with additional classes */}
                <div className="space-y-4">
                  <Input
                    label="Full Name"
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Enter your full name"
                    required
                    className="border-gray-600 focus:ring-purple-500 focus:border-transparent"
                    labelClassName="text-gray-300"
                  />
                  
                  <Input
                    label="Email Address"
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="you@example.com"
                    helperText={
                      mode === 'availability' 
                        ? "We'll email you when someone matches with your available times." 
                        : "We'll use this for class information and updates."
                    }
                    required
                    className="border-gray-600 focus:ring-purple-500 focus:border-transparent"
                    labelClassName="text-gray-300 bg-transparent"
                    helperTextClassName="text-xs text-gray-400 mt-1"
                  />
                  
                  <Input
                    label="Phone Number (for SMS)"
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="+852 1234-5678"
                    required
                    className="border-gray-600 focus:ring-purple-500 focus:border-transparent"
                    labelClassName="text-gray-300 bg-transparent"
                  />
                </div>
                
                <div className="flex justify-between pt-4 mt-2">
                  {/* Use your Button component with custom styles */}
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={handleBack}
                    className="bg-gray-700 hover:bg-gray-600 border border-gray-600"
                  >
                    Back
                  </Button>
                  
                  <Button
                    type="submit"
                    variant="primary"
                    isLoading={isSubmitting}
                    className="bg-purple-600 hover:bg-purple-700"
                  >
                    {getButtonText()}
                  </Button>
                </div>
              </div>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
};

export default ConfirmationPage;